/* Horizon Optics Italia V3: native Shopify cart drawer adapted from approved SOVENIRA V27. */
if (!customElements.get('cart-drawer')) {
  class HorizonCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.lastTrigger = null;
      this.onDocumentKeydown = (event) => {
        if (!this.classList.contains('active')) return;
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapTab(event);
      };
      this.onDocumentClick = (event) => {
        const link = event.target.closest('#cart-icon-bubble, .ony-cart');
        if (!link) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(link);
      };
      this.addEventListener('click', this.onDrawerClick.bind(this));
      this.addEventListener('change', this.onDrawerChange.bind(this));
      this.addEventListener('submit', this.onDrawerSubmit.bind(this));
    }

    connectedCallback() {
      document.addEventListener('click', this.onDocumentClick);
      const cartLink = document.getElementById('cart-icon-bubble');
      if (cartLink) { cartLink.setAttribute('aria-haspopup', 'dialog'); }
      document.addEventListener('keydown', this.onDocumentKeydown);
      this.setupUpsellCarousel();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
    }

    setActiveElement(element) { this.lastTrigger = element; }

    getSectionsToRender() {
      return [{ id: 'cart-drawer' }, { id: 'cart-icon-bubble' }];
    }

    getRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    trapTab(event) {
      const panel = this.querySelector('.sov-cart__panel');
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),summary,[tabindex="0"]')).filter((el) => el.getClientRects().length);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      this.classList.add('active');
      document.body.classList.add('sov-cart-open');
      const panel = this.querySelector('.sov-cart__panel');
      if (panel) panel.focus({ preventScroll: true });
      this.setupUpsellCarousel();
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('sov-cart-open');
      if (this.lastTrigger && document.contains(this.lastTrigger)) this.lastTrigger.focus({ preventScroll: true });
    }

    async request(endpoint, data) {
      const response = await fetch(this.getRoot() + 'cart/' + endpoint + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ ...data, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname })
      });
      const result = await response.json();
      if (!response.ok || result.status >= 400 || result.errors) {
        throw new Error(result.description || result.message || 'Impossibile aggiornare il carrello. Riprova.');
      }
      return result;
    }

    async renderContents(parsedState) {
      const section = parsedState && parsedState.sections && parsedState.sections['cart-drawer'];
      if (section) {
        const parsed = new DOMParser().parseFromString(section, 'text/html');
        const replacement = parsed.querySelector('#CartDrawer');
        const existing = this.querySelector('#CartDrawer');
        if (replacement && existing) {
          const oldScroll = existing.querySelector('.sov-cart__scroll');
          const scrollTop = oldScroll ? oldScroll.scrollTop : 0;
          existing.replaceWith(replacement);
          const newScroll = replacement.querySelector('.sov-cart__scroll');
          if (newScroll) newScroll.scrollTop = scrollTop;
          this.classList.toggle('is-empty', !replacement.querySelector('.sov-cart__item'));
        }
      } else {
        await this.refreshSections();
      }
      await this.updateHeaderCount(parsedState);
      this.open();
    }

    async refreshSections() {
      const result = await fetch(this.getRoot() + '?sections=cart-drawer,cart-icon-bubble', { headers: { Accept: 'application/json' } });
      if (!result.ok) throw new Error('Impossibile aggiornare il carrello.');
      const sections = await result.json();
      if (!sections['cart-drawer']) throw new Error('La sezione carrello non è disponibile.');
      const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const replacement = parsed.querySelector('#CartDrawer');
      const existing = this.querySelector('#CartDrawer');
      if (!replacement || !existing) throw new Error('Il modello del carrello non è disponibile.');
      existing.replaceWith(replacement);
      this.classList.toggle('is-empty', !replacement.querySelector('.sov-cart__item'));
      await this.updateHeaderCount({ sections });
    }

    async pruneOrphanProtection(result) {
      const root = new DOMParser().parseFromString(result?.sections?.['cart-drawer'] || '', 'text/html').querySelector('#CartDrawer');
      if (root && Number(root.dataset.sovRegularCount) !== 0) return result;
      const response = await fetch(this.getRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return result;
      const cart = await response.json();
      const handle = this.dataset.sovProtectionHandle || 'shipping-protection';
      if (cart.items.some(item => item.handle !== handle)) return result;
      let latest = result;
      for (const item of cart.items.filter(item => item.handle === handle)) {
        latest = await this.request('change', { id: item.key, quantity: 0 });
      }
      return latest;
    }

    async updateHeaderCount(state) {
      const anchor = document.getElementById('cart-icon-bubble');
      if (!anchor) return;
      // Update the live Horizon header icon and its badge from Shopify's server-rendered section.
      const bubbleHtml = state?.sections?.['cart-icon-bubble'];
      if (bubbleHtml) {
        const parsed = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        const section = parsed.querySelector('.shopify-section');
        if (section) anchor.innerHTML = section.innerHTML;
      }
      let count = Number.isFinite(state?.item_count) ? state.item_count : null;
      if (count === null) {
        try {
          const response = await fetch(this.getRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
          if (response.ok) count = (await response.json()).item_count;
        } catch (e) { /* Keep the drawer usable if the badge request fails. */ }
      }
      const visibleCount = this.querySelector('#CartDrawer')?.dataset?.sovRegularCount;
      if (visibleCount !== undefined) count = Math.max(0, Number(visibleCount) || 0);
      if (count !== null) {
        anchor.setAttribute('aria-label', `Carrello: ${count} articoli`);
        const bubble = anchor.querySelector('.cart-count-bubble');
        if (count === 0 && bubble) bubble.remove();
        else if (count > 0 && bubble) {
          const shown = bubble.querySelector('[aria-hidden="true"]');
          if (shown) shown.textContent = count < 100 ? String(count) : '';
          const accessible = bubble.querySelector('.visually-hidden');
          if (accessible) accessible.textContent = `${count} articoli`;
        }

        // Safe fallback when Shopify omitted a section response.
        if (!bubbleHtml) {
          const oldBubble = anchor.querySelector('.cart-count-bubble');
          if (count === 0 && oldBubble) oldBubble.remove();
          else if (count > 0 && oldBubble) {
            const number = oldBubble.querySelector('[aria-hidden="true"]');
            if (number) number.textContent = count < 100 ? String(count) : '';
          }
        }
      }
    }

    showNotice(message, isError = false) {
      const place = this.querySelector('#SovCartNotice');
      if (!place) return;
      place.textContent = message;
      place.classList.toggle('sov-cart__error--alert', Boolean(isError));
    }

    async transact(fn, button) {
      if (this.busy) return;
      this.busy = true;
      this.classList.add('is-busy');
      if (button) button.disabled = true;
      try { await fn(); }
      catch (error) { this.showNotice(error.message || 'Si è verificato un problema. Riprova.', true); }
      finally { this.busy = false; this.classList.remove('is-busy'); if (button && document.contains(button)) button.disabled = false; }
    }

    updateUpsellCarousel(track) {
      if (!track || !track.isConnected) return;
      const section = track.closest('[data-sov-upsell-section]');
      if (!section) return;
      const count = track.querySelectorAll('[data-sov-upsell-card]').length;
      section.dataset.sovUpsellCount = String(count);
      const previous = section.querySelector('[data-sov-upsell-prev]');
      const next = section.querySelector('[data-sov-upsell-next]');
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 5;
      if (previous) previous.disabled = count < 2 || track.scrollLeft <= 5;
      if (next) next.disabled = count < 2 || atEnd;
    }

    makeRecommendationCard(product, currency) {
      if (!product || !product.available || !Array.isArray(product.variants)) return null;
      const variants = product.variants.filter(v => v.available && Number.isFinite(Number(v.id)));
      if (!variants.length) return null;
      const first = variants[0];
      const money = (cents) => {
        try { return new Intl.NumberFormat('it-IT', { style: 'currency', currency: currency || 'EUR' }).format(Number(cents) / 100); }
        catch (e) { return (Number(cents) / 100).toFixed(2) + ' ' + (currency || 'EUR'); }
      };
      const card = document.createElement('article');
      card.className = 'sov-cart__upsell sov-cart__upsell-card';
      card.dataset.sovUpsellCard = '';
      card.dataset.sovUpsellHandle = product.handle;
      const href = typeof product.url === 'string' && product.url.startsWith('/') ? product.url : this.getRoot() + 'products/' + encodeURIComponent(product.handle);
      const photo = document.createElement('a');
      photo.className = 'sov-cart__upsell-image';
      photo.href = href;
      photo.setAttribute('aria-label', product.title);
      if (product.featured_image) {
        const img = document.createElement('img');
        const url = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.src;
        if (typeof url === 'string' && (url.startsWith('//') || url.startsWith('https://') || url.startsWith('/'))) {
          img.src = url; img.alt = product.title; img.loading = 'lazy'; img.width = 140; img.height = 140;
          photo.appendChild(img);
        }
      }
      const info = document.createElement('div'); info.className = 'sov-cart__upsell-info';
      const title = document.createElement('a'); title.href = href; title.className = 'sov-cart__upsell-title'; title.textContent = product.title;
      info.appendChild(title);
      if (variants.length > 1) {
        const id = 'SovAutoUpsellVariant-' + String(product.id);
        const label = document.createElement('label'); label.className = 'sov-cart__upsell-option-label'; label.htmlFor = id; label.textContent = 'Seleziona la variante';
        const select = document.createElement('select'); select.className = 'sov-cart__upsell-select'; select.id = id; select.dataset.sovUpsellSelect = '';
        variants.forEach(v => {
          const option = document.createElement('option'); option.value = String(v.id);
          option.textContent = v.public_title || v.title || 'Variante';
          option.dataset.price = money(v.price);
          option.dataset.compare = Number(v.compare_at_price) > Number(v.price) ? money(v.compare_at_price) : '';
          select.appendChild(option);
        });
        info.append(label, select);
      }
      const bottom = document.createElement('div'); bottom.className = 'sov-cart__upsell-bottom';
      const prices = document.createElement('div'); prices.className = 'sov-cart__upsell-prices';
      const price = document.createElement('strong'); price.dataset.sovUpsellPrice = ''; price.textContent = money(first.price);
      const compare = document.createElement('s'); compare.dataset.sovUpsellCompare = '';
      compare.textContent = Number(first.compare_at_price) > Number(first.price) ? money(first.compare_at_price) : '';
      compare.hidden = !compare.textContent;
      prices.append(price, compare);
      const add = document.createElement('button'); add.type = 'button'; add.className = 'sov-cart__upsell-add'; add.dataset.sovAdd = String(first.id); add.textContent = '+ AGGIUNGI';
      bottom.append(prices, add); info.appendChild(bottom); card.append(photo, info);
      return card;
    }

    setupUpsellCarousel() {
      const section = this.querySelector('[data-sov-upsell-section]');
      const track = section && section.querySelector('[data-sov-upsell-track]');
      if (!track) return;
      if (!track.dataset.sovScrollBound) {
        track.dataset.sovScrollBound = 'true';
        track.addEventListener('scroll', () => this.updateUpsellCarousel(track), { passive: true });
      }
      this.updateUpsellCarousel(track);
      if (section.dataset.sovUpsellAuto === 'false' || track.dataset.sovRequested === 'true') return;
      const productId = Number(track.dataset.sovRecommendProductId);
      if (!Number.isInteger(productId) || productId < 1) return;
      track.dataset.sovRequested = 'true';
      const url = (intent) => this.getRoot() + 'recommendations/products.json?product_id=' + productId + '&limit=10&intent=' + intent;
      const getProducts = async (intent) => {
        const result = await fetch(url(intent), { headers: { Accept: 'application/json' } });
        if (!result.ok) return [];
        const json = await result.json();
        return Array.isArray(json.products) ? json.products : [];
      };
      Promise.allSettled([getProducts('complementary'), getProducts('related')]).then(results => {
        if (!track.isConnected) return;
        const existing = new Set(Array.from(track.querySelectorAll('[data-sov-upsell-handle]')).map(el => el.dataset.sovUpsellHandle));
        const cartHandles = new Set(Array.from(this.querySelectorAll('[data-sov-cart-handle]')).map(el => el.dataset.sovCartHandle));
        cartHandles.add('shipping-protection');
        const currency = track.dataset.sovCurrency || 'EUR';
        // Add complementary recommendations first, then relevant alternative glasses.
        for (const result of results) {
          if (result.status !== 'fulfilled') continue;
          for (const product of result.value) {
            if (track.querySelectorAll('[data-sov-upsell-card]').length >= 12) break;
            if (!product?.handle || existing.has(product.handle) || cartHandles.has(product.handle)) continue;
            const card = this.makeRecommendationCard(product, currency);
            if (!card) continue;
            existing.add(product.handle);
            track.appendChild(card);
          }
        }
        this.updateUpsellCarousel(track);
      }).catch(() => { /* Keep curated products if recommendations are unavailable. */ });
    }

    onDrawerClick(event) {
      const close = event.target.closest('[data-sov-close]');
      if (close) { event.preventDefault(); this.close(); return; }
      const q = event.target.closest('[data-sov-quantity]');
      if (q) {
        event.preventDefault();
        const current = Number(q.dataset.sovCurrent);
        const step = Number(q.dataset.sovStep) || 1;
        const minimum = Number(q.dataset.sovMin) || 1;
        const maximum = q.dataset.sovMax ? Number(q.dataset.sovMax) : Infinity;
        const proposed = current + (q.dataset.sovQuantity === 'minus' ? -step : step);
        const quantity = Math.max(minimum, Math.min(maximum, proposed));
        if (quantity === current) return;
        return this.transact(async () => this.renderContents(await this.request('change', { id: q.dataset.sovKey, quantity })), q);
      }
      const remove = event.target.closest('[data-sov-remove]');
      if (remove) {
        event.preventDefault();
        return this.transact(async () => {
          const result = await this.request('change', { id: remove.dataset.sovRemove, quantity: 0 });
          await this.renderContents(await this.pruneOrphanProtection(result));
        }, remove);
      }
      const carouselNav = event.target.closest('[data-sov-upsell-prev], [data-sov-upsell-next]');
      if (carouselNav) {
        event.preventDefault();
        const section = carouselNav.closest('[data-sov-upsell-section]');
        const track = section && section.querySelector('[data-sov-upsell-track]');
        const firstCard = track && track.querySelector('[data-sov-upsell-card]');
        if (!firstCard) return;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const distance = firstCard.getBoundingClientRect().width + gap;
        track.scrollBy({ left: distance * (carouselNav.matches('[data-sov-upsell-next]') ? 1 : -1), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        return;
      }
      const add = event.target.closest('[data-sov-add]');
      if (add) {
        event.preventDefault();
        const card = add.closest('[data-sov-upsell-card]');
        const select = card && card.querySelector('[data-sov-upsell-select]');
        const variantId = Number(select ? select.value : add.dataset.sovAdd);
        if (!Number.isInteger(variantId) || variantId <= 0) return this.showNotice('Seleziona una variante valida.', true);
        return this.transact(async () => this.renderContents(await this.request('add', { items: [{ id: variantId, quantity: 1 }] })), add);
      }
      const protection = event.target.closest('[data-sov-protection-toggle]');
      if (protection) {
        event.preventDefault();
        const variantId = Number(protection.dataset.sovProtectionVariant);
        const currentKey = protection.dataset.sovProtectionKey;
        const handle = protection.dataset.sovProtectionHandle || 'shipping-protection';
        if (!Number.isInteger(variantId) || variantId <= 0) return this.showNotice('La protezione non è disponibile.', true);
        return this.transact(async () => {
          let result;
          if (protection.getAttribute('aria-checked') === 'true') {
            // Remove every protection line, including any duplicate Shopify property lines.
            const response = await fetch(this.getRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
            if (!response.ok) throw new Error('Impossibile leggere il carrello.');
            const cart = await response.json();
            const lines = cart.items.filter((item) => item.handle === handle || item.variant_id === variantId);
            if (!lines.length && currentKey) lines.push({ key: currentKey });
            for (const line of lines) result = await this.request('change', { id: line.key, quantity: 0 });
            if (!result) return this.refreshSections();
          } else {
            result = await this.request('add', { items: [{ id: variantId, quantity: 1 }] });
          }
          await this.renderContents(result);
        }, protection);
      }
      const promo = event.target.closest('[data-sov-fill-code]');
      if (promo) {
        event.preventDefault();
        const details = this.querySelector('#SovCartCode');
        const input = this.querySelector('#SovCartDiscountInput');
        if (details) details.open = true;
        if (input) { input.value = promo.dataset.sovFillCode; input.focus(); }
      }
    }

    onDrawerChange(event) {
      if (!event.target.matches('[data-sov-upsell-select]')) return;
      const option = event.target.selectedOptions[0];
      if (!option) return;
      const card = event.target.closest('[data-sov-upsell-card]');
      if (!card) return;
      const price = card.querySelector('[data-sov-upsell-price]');
      const compare = card.querySelector('[data-sov-upsell-compare]');
      const add = card.querySelector('[data-sov-add]');
      if (price) price.textContent = option.dataset.price || '';
      if (compare) { compare.textContent = option.dataset.compare || ''; compare.hidden = !option.dataset.compare; }
      if (add) { add.dataset.sovAdd = option.value; add.disabled = option.disabled; }
    }

    onDrawerSubmit(event) {
      const form = event.target.closest('[data-sov-discount-form]');
      if (!form) return;
      event.preventDefault();
      const input = form.querySelector('[name="discount"]');
      const code = (input?.value || '').trim();
      if (!code) { this.showNotice('Inserisci prima un codice sconto.', true); return; }
      const button = form.querySelector('[type="submit"]');
      return this.transact(async () => {
        const cart = await this.request('update', { discount: code });
        await this.renderContents(cart);
        const details = this.querySelector('#SovCartCode');
        if (details) details.open = true;
        this.showNotice('Codice inviato. La validità sarà verificata al checkout.');
      }, button);
    }
  }
  customElements.define('cart-drawer', HorizonCartDrawer);
}
